import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import Swal from 'sweetalert2'
import RozliczeniaService from '@/services/RozliczeniaService'
import SettingsService from '@/services/SettingsService'

const createEmptyEntry = (prefix = 'entry', withCompany = false) => ({
	_id: `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	date: moment().format('YYYY-MM-DD'),
	note: '',
	value: '',
	...(withCompany ? { companyId: '', companyName: '' } : {}),
})

const normalizeEntries = (entries, prefix = 'entry', withCompany = false) => (
	Array.isArray(entries) ? entries : []
).map((item, index) => ({
	_id: item?._id || `${prefix}-${index}-${Date.now()}`,
	date: item?.date ? moment(item.date).format('YYYY-MM-DD') : '',
	note: `${item?.note || ''}`,
	value: item?.value == null ? '' : `${item.value}`,
	...(withCompany ? {
		companyId: `${item?.companyId || ''}`,
		companyName: `${item?.companyName || ''}`,
	} : {}),
}))

const sumRows = rows => rows.reduce((sum, item) => {
	const value = Number(`${item.value}`.replace(',', '.'))
	return sum + (Number.isFinite(value) ? value : 0)
}, 0)

const toPayloadRows = (rows, withCompany = false) => rows
	.filter(item => item.date && `${item.value}`.trim() !== '')
	.map(item => ({
		date: item.date,
		note: item.note,
		value: Number(`${item.value}`.replace(',', '.')),
		...(withCompany ? {
			companyId: `${item.companyId || ''}`.trim(),
			companyName: `${item.companyName || ''}`.trim(),
		} : {}),
	}))

const patchRow = (item, field, value) => {
	if (field && typeof field === 'object' && value === undefined) {
		return { ...item, ...field }
	}
	return { ...item, [field]: value }
}

export default function useRozliczeniaDetails() {
	const { t } = useTranslation()
	const { trainerId } = useParams()
	const now = moment()

	const [year, setYear] = useState(now.year())
	const [month, setMonth] = useState(now.month() + 1)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [trainer, setTrainer] = useState(null)
	const [realizationRows, setRealizationRows] = useState([])
	const [totals, setTotals] = useState(null)
	const [entries, setEntries] = useState([])
	const [przelewy, setPrzelewy] = useState([])
	const [rozliczono, setRozliczono] = useState(false)
	const [unsettledMonths, setUnsettledMonths] = useState([])
	const [systemSettings, setSystemSettings] = useState(null)

	const companies = useMemo(() => {
		if (!Array.isArray(systemSettings?.companies)) return []
		return systemSettings.companies
			.filter(company => company?.id)
			.map(company => ({ id: company.id, name: company.companyName || company.id }))
			.sort((a, b) => a.name.localeCompare(b.name, 'pl'))
	}, [systemSettings])

	useEffect(() => {
		SettingsService.getSystem().then(({ data }) => {
			setSystemSettings(data || null)
		})
	}, [])

	const loadData = useCallback(async () => {
		if (!trainerId) return
		setLoading(true)
		setError(null)

		const result = await RozliczeniaService.getTrainerMonth(trainerId, { year, month })
		if (result.error) {
			setError(result.error)
			setTrainer(null)
			setRealizationRows([])
			setEntries([])
			setPrzelewy([])
			setTotals(null)
			setRozliczono(false)
			setUnsettledMonths([])
		} else {
			setTrainer(result.data?.trainer || null)
			setRealizationRows(Array.isArray(result.data?.realizationRows) ? result.data.realizationRows : [])
			setEntries(normalizeEntries(result.data?.entries, 'entry'))
			setPrzelewy(normalizeEntries(result.data?.przelewy, 'przelew', true))
			setTotals(result.data?.totals || null)
			setRozliczono(!!result.data?.rozliczono)
			setUnsettledMonths(Array.isArray(result.data?.unsettledMonths) ? result.data.unsettledMonths : [])
		}

		setLoading(false)
	}, [month, trainerId, year])

	useEffect(() => {
		loadData()
	}, [loadData])

	useEffect(() => {
		if (!error) return
		Swal.fire({
			title: t('notifications.errorTitle'),
			text: error,
			icon: 'error',
		}).then(() => setError(null))
	}, [error, t])

	const localTotals = useMemo(() => {
		const base = {
			kwota_realizacji: totals?.kwota_realizacji || 0,
			koszt_noclegu: totals?.koszt_noclegu || 0,
			dodatkowe_koszty: totals?.dodatkowe_koszty || 0,
			suma_km: totals?.suma_km || 0,
		}
		const projects = base.kwota_realizacji
			+ base.koszt_noclegu
			+ base.dodatkowe_koszty
			+ base.suma_km
		const entriesTotal = sumRows(entries)
		const przelewyTotal = sumRows(przelewy)

		return {
			...base,
			projects,
			entries: entriesTotal,
			przelewy: przelewyTotal,
			do_rozliczenia: projects + entriesTotal - przelewyTotal,
		}
	}, [entries, przelewy, totals])

	const addEntry = useCallback(() => {
		setEntries(prev => [...prev, createEmptyEntry('entry')])
	}, [])

	const updateEntry = useCallback((entryId, field, value) => {
		setEntries(prev => prev.map(item => (
			item._id === entryId ? patchRow(item, field, value) : item
		)))
	}, [])

	const removeEntry = useCallback(entryId => {
		setEntries(prev => prev.filter(item => item._id !== entryId))
	}, [])

	const addPrzelew = useCallback(() => {
		const defaultCompany = companies.length === 1 ? companies[0] : null
		setPrzelewy(prev => [...prev, {
			...createEmptyEntry('przelew', true),
			companyId: defaultCompany?.id || '',
			companyName: defaultCompany?.name || '',
		}])
	}, [companies])

	const updatePrzelew = useCallback((entryId, field, value) => {
		setPrzelewy(prev => prev.map(item => (
			item._id === entryId ? patchRow(item, field, value) : item
		)))
	}, [])

	const removePrzelew = useCallback(entryId => {
		setPrzelewy(prev => prev.filter(item => item._id !== entryId))
	}, [])

	const save = useCallback(async () => {
		setSaving(true)

		const result = await RozliczeniaService.saveTrainerMonth(trainerId, {
			year,
			month,
			rozliczono,
			entries: toPayloadRows(entries),
			przelewy: toPayloadRows(przelewy, true),
		})

		setSaving(false)

		if (!result.success) {
			await Swal.fire({
				title: t('notifications.errorTitle'),
				text: result.error || t('rozliczenia.details.saveError'),
				icon: 'error',
			})
			return
		}

		await Swal.fire({
			title: t('rozliczenia.details.saveSuccessTitle'),
			text: t('rozliczenia.details.saveSuccessText'),
			icon: 'success',
			timer: 1500,
			showConfirmButton: false,
		})
		await loadData()
	}, [entries, loadData, month, przelewy, rozliczono, t, trainerId, year])

	const goPrevMonth = useCallback(() => {
		const previous = moment({ year, month: month - 1 }).subtract(1, 'month')
		setYear(previous.year())
		setMonth(previous.month() + 1)
	}, [month, year])

	const goNextMonth = useCallback(() => {
		const next = moment({ year, month: month - 1 }).add(1, 'month')
		setYear(next.year())
		setMonth(next.month() + 1)
	}, [month, year])

	const selectMonth = useCallback((nextYear, nextMonth) => {
		setYear(nextYear)
		setMonth(nextMonth)
	}, [])

	return {
		trainer,
		loading,
		saving,
		year,
		month,
		unsettledMonths,
		realizationRows,
		entries,
		przelewy,
		companies,
		rozliczono,
		setRozliczono,
		totals: localTotals,
		addEntry,
		updateEntry,
		removeEntry,
		addPrzelew,
		updatePrzelew,
		removePrzelew,
		save,
		goPrevMonth,
		goNextMonth,
		selectMonth,
		setYear,
		setMonth,
	}
}
